import type { RefObject } from "react"
import { useEffect, useRef } from "react"
import { focusedCourseId } from "./focused-lesson"
import { holdSlotOnLine, scrollParentOf, stickyInsetOf } from "./hold-slot-on-line"

/**
 * Brings this slot to the top of its scroller when it is the focused course, and holds it there
 * while the rest of the day's lessons mount around it.
 *
 * The hold is the whole point. The month sidebar's slots are separate React roots that paint one
 * at a time, so a slot scrolled into place on mount is shoved back down by every sibling that
 * lands after it. The Ember host used to chase that by re-scrolling on each lesson's
 * `{{did-insert}}` — which fired on the wrapper, not on content, and so measured a slot that had
 * not rendered yet. Riding the reflow itself needs no load signal at all.
 *
 * Slot-owned, like the post-drop anchor: the slot knows when it exists and when it moves, and
 * nothing above it in the planner does.
 */
export function useFocusedLessonScroll(
  slotRef: RefObject<HTMLElement | null>,
  courseId: string,
): void {
  // A tracked-render read: re-renders this slot when the month focuses a different course.
  const isFocused = focusedCourseId() === courseId
  // A focus change the user asked for animates; arriving already-focused does not, or the sidebar
  // would slide up from the top every time the day changes.
  const hasArmed = useRef(false)

  useEffect(() => {
    const slot = slotRef.current
    if (!isFocused || !slot) return

    const scroller = scrollParentOf(slot)
    const lineOf = (): number => scroller.getBoundingClientRect().top + stickyInsetOf(scroller)

    const drift = slot.getBoundingClientRect().top - lineOf()
    if (Math.abs(drift) >= 1) {
      scroller.scrollTo({
        top: scroller.scrollTop + drift,
        behavior: hasArmed.current ? "smooth" : "instant",
      })
    }
    hasArmed.current = true

    // Corrections only ever come from a reflow, so a smooth scroll with nothing moving under it
    // runs to completion untouched.
    return holdSlotOnLine(slot, lineOf, () => {})
  }, [isFocused, slotRef])
}
